import { useState, useEffect, useCallback } from 'react';
import { coursesApi } from '../../courses/services/coursesApi';

/**
 * @typedef {Object} AskGraspdHistoryItem
 * @property {string} id - Unique ID for this interaction
 * @property {string} selectedText - The text that was selected
 * @property {number} startOffset - Character offset where selection starts
 * @property {number} endOffset - Character offset where selection ends
 * @property {'EXPLAIN' | 'EXAMPLE' | 'DEEPER' | 'WHY' | 'HOW' | 'CUSTOM'} questionType
 * @property {string} [customQuestion] - Custom question if questionType was CUSTOM
 * @property {string} answer - The AI response (markdown)
 * @property {string} createdAt - ISO-8601 timestamp
 */

/**
 * Hook for fetching and managing Ask Graspd history for a section.
 * Call this when loading a section to render inline highlights for previous questions.
 *
 * @param {string | null} sectionId - The section ID to fetch history for
 * @returns {{
 *   history: AskGraspdHistoryItem[],
 *   isLoading: boolean,
 *   error: string | null,
 *   refetch: () => Promise<void>,
 *   addToHistory: (item: AskGraspdHistoryItem) => void
 * }}
 */
export function useAskGraspdHistory(sectionId) {
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    if (!sectionId) {
      setHistory([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await coursesApi.fetchAskGraspdHistory({
        sectionId,
        auth: true,
      });
      setHistory(data.items || []);
    } catch (err) {
      console.error('[useAskGraspdHistory] Error fetching history:', err);
      // Don't show error to user for history fetch failures - just use empty history
      setHistory([]);
      if (err?.status !== 404) {
        setError('Failed to load previous questions');
      }
    } finally {
      setIsLoading(false);
    }
  }, [sectionId]);

  // Fetch history when sectionId changes
  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Add a new item to history (called after successful askGraspd response)
  const addToHistory = useCallback((item) => {
    setHistory((prev) => [item, ...prev]);
  }, []);

  return {
    history,
    isLoading,
    error,
    refetch: fetchHistory,
    addToHistory,
  };
}
